// React
import React, { useEffect, useCallback, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Services
import {
  fetchProgramIndicators,
  addProgramIndicatorFromTemplate as addProgramIndicatorFromTemplateService,
  addProgramIndicatorFromTemplatesGroup as addProgramIndicatorFromTemplatesGroupService,
} from '@services/programServices';

// Actions
import { clearProgramIndicators as clearProgramIndicatorsAction } from '@actions/programActions';

// Elements
import GuardedComponent from '@components/molecules/GuardedComponent/GuardedComponent';
import TableTemplate from '@templates/TableTemplate';
import FloatingButton from '@components/atoms/FloatingButton/FloatingButton';
import {
  COORDINATOR,
  // PROGRAM_COORDINATOR,
  // TASK_COORDINATOR,
  // ORGANIZATION_ADMINISTRATOR,
  // PROGRAM_GUARDIAN,
} from '@constants/roles';
import BulkIndicatorDialog from './BulkIndicatorDialog';
import ProgramIndicatorTemplateDialog from './ProgramIndicatorTemplateDialog';

// Data
const headCells = [
  { type: 'indicator_name', label: 'Nazwa wskaźnika' },
  { type: 'indicator_kind', label: 'Rodzaj wskaźnika' },
  { type: 'indicator_type', label: 'Rodzaj pomiaru' },
  { type: 'indicator_time_period', label: 'Przedział czasowy' },
  { type: 'aggregation', label: 'Przypisanie' },
  { type: 'task', label: 'Zadanie' },
  { type: 'planned_number_of_measurements', label: 'Planowana liczba pomiarów' },
];

// Component
const ProgramIndicators = ({ clearProgramIndicators, fetchIndicators, data }) => {
  // const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { indicators } = data;
  // state for bulk indicator measurements dialog
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  // state for adding indicator from template or group
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);

  useEffect(() => {
    clearProgramIndicators();
  }, []);

  const triggerFetchIndicators = useCallback((myid) => fetchIndicators(myid), [fetchIndicators]);

  useEffect(() => {
    triggerFetchIndicators(id);
  }, [id, triggerFetchIndicators]);
  /**
   * Open bulk measurements dialog for a given indicator
   */
  const handleOpenBulk = useCallback((indicator) => {
    setSelectedIndicator(indicator);
    setOpenBulkDialog(true);
  }, []);
  /**
   * Close bulk measurements dialog
   */
  const handleCloseBulk = useCallback(() => {
    setOpenBulkDialog(false);
    setSelectedIndicator(null);
  }, []);

  // row actions: three-dots menu for bulk adding of measurements
  const rowActions = [
    {
      name: 'Uzupełnij pomiary wskaźnika',
      action: handleOpenBulk,
    },
  ];
  return (
    <>
      <TableTemplate
        headCells={headCells}
        data={indicators}
        isnav
        link="/wskazniki"
        tableName="Wskaźniki"
        rowActions={rowActions}
      />
      {/* Dialog to select template for adding to program */}
      <ProgramIndicatorTemplateDialog
        open={openTemplateDialog}
        onClose={() => setOpenTemplateDialog(false)}
        onConfirm={(selection) => {
          if (selection.type === 'template') {
            dispatch(addProgramIndicatorFromTemplateService(id, selection.id))
              .then(() => triggerFetchIndicators(id))
              .catch((err) => console.error(err));
          } else if (selection.type === 'group') {
            dispatch(addProgramIndicatorFromTemplatesGroupService(id, selection.id))
              .then(() => triggerFetchIndicators(id))
              .catch((err) => console.error(err));
          }
          setOpenTemplateDialog(false);
        }}
      />
      {/* Dialog for bulk adding of indicator measurements */}
      <BulkIndicatorDialog
        open={openBulkDialog}
        onClose={handleCloseBulk}
        indicator={selectedIndicator}
        programId={id}
      />
      <GuardedComponent
        allowed_user_roles={[
          COORDINATOR,
          // ORGANIZATION_ADMINISTRATOR,
          // PROGRAM_GUARDIAN,
          // PROGRAM_COORDINATOR,
          // TASK_COORDINATOR,
        ]}
      >
        <FloatingButton onClick={() => setOpenTemplateDialog(true)} />
      </GuardedComponent>
    </>
  );
};

ProgramIndicators.propTypes = {
  clearProgramIndicators: PropTypes.func,
  fetchIndicators: PropTypes.func,
  data: PropTypes.shape({
    id: PropTypes.number,
    program_name: PropTypes.string,
    indicators: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        indicator_name: PropTypes.string,
        indicator_kind: PropTypes.string,
        indicator_type: PropTypes.string,
        indicator_time_period: PropTypes.string,
        planned_number_of_measurements: PropTypes.number,
      }),
    ),
  }),
};

ProgramIndicators.defaultProps = {
  clearProgramIndicators: null,
  fetchIndicators: null,
  data: {
    id: null,
    program_name: '',
    indicators: [],
  },
};
const mapStateToProps = ({ programReducer }) => ({
  data: programReducer.indicators,
});

const mapDispatchToProps = (dispatch) => ({
  clearProgramIndicators: () => dispatch(clearProgramIndicatorsAction()),
  fetchIndicators: (id) => dispatch(fetchProgramIndicators(id)),
  addProgramIndicatorFromTemplate: (programId, templateId) =>
    dispatch(addProgramIndicatorFromTemplateService(programId, templateId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProgramIndicators);
